"use client";

import { c as _c } from "react/compiler-runtime";
import { motion } from "motion/react";
import { useEffect, useId, useMemo, useState } from "react";
import { chartCssVars, useChart } from "./chart-context";
export type BarLineCap = "round" | "butt" | number;
export type BarAnimationType = "grow" | "fade";
export interface BarProps {
  /** Key in data to use for y values */
  dataKey: string;
  /** Fill color for the bar. Can be a color, gradient url, or pattern url. Default: var(--chart-line-primary) */
  fill?: string;
  /** Color for tooltip dot. Use when fill is a gradient/pattern. Default: uses fill value */
  stroke?: string;
  /** Line cap style for bar ends: "round", "butt", or a number for custom radius. Default: "round" */
  lineCap?: BarLineCap;
  /** Whether to animate the bars. Default: true */
  animate?: boolean;
  /** Animation type: "grow" (height) or "fade" (opacity + blur). Default: "grow" */
  animationType?: BarAnimationType;
  /** Opacity when not hovered (when another bar is hovered). Default: 0.3 */
  fadedOpacity?: number;
  /** Stagger delay between bars in seconds. Auto-calculated if not provided. */
  staggerDelay?: number;
  /** Gap between stacked bars in pixels. Default: 0 */
  stackGap?: number;
  /** Gap between grouped bars in pixels. Default: 4 */
  groupGap?: number;
}

// Same easing as Line chart for consistent animation feel
const BAR_EASING = "cubic-bezier(0.85, 0, 0.15, 1)";
interface AnimatedBarProps {
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  rx: number;
  ry: number;
  index: number;
  isFaded: boolean;
  animationType: BarAnimationType;
  innerHeight: number;
  fadedOpacity: number;
  staggerDelay: number;
  animationDuration: number;
  isHorizontal: boolean;
}
function AnimatedBar(t0) {
  const $ = _c(50);
  const {
    x,
    y,
    width,
    height,
    fill,
    rx,
    ry,
    index,
    isFaded,
    animationType,
    innerHeight,
    fadedOpacity,
    staggerDelay,
    animationDuration,
    isHorizontal
  } = t0;
  const [isAnimated, setIsAnimated] = useState(false);
  let t1;
  let t2;
  if ($[0] !== index || $[1] !== staggerDelay) {
    t1 = () => {
      const timeout = setTimeout(() => {
        setIsAnimated(true);
      }, index * staggerDelay * 1000);
      return () => clearTimeout(timeout);
    };
    t2 = [index, staggerDelay];
    $[0] = index;
    $[1] = staggerDelay;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const barDuration = animationDuration * 0.6;
  let t3;
  if ($[4] !== fadedOpacity || $[5] !== isAnimated || $[6] !== isFaded) {
    t3 = () => {
      if (isFaded) {
        return fadedOpacity;
      }
      return isAnimated ? 1 : 0;
    };
    $[4] = fadedOpacity;
    $[5] = isAnimated;
    $[6] = isFaded;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const getFadeOpacity = t3;
  if (animationType === "fade") {
    let t4;
    if ($[8] !== getFadeOpacity) {
      t4 = getFadeOpacity();
      $[8] = getFadeOpacity;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    const t5 = isAnimated ? "blur(0px)" : "blur(2px)";
    let t6;
    if ($[10] !== t4 || $[11] !== t5) {
      t6 = {
        opacity: t4,
        filter: t5
      };
      $[10] = t4;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        opacity: 0,
        filter: "blur(2px)"
      };
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    const t8 = `opacity ${barDuration}ms ${BAR_EASING}, filter ${barDuration}ms ${BAR_EASING}`;
    let t9;
    if ($[14] !== t8) {
      t9 = {
        transition: t8
      };
      $[14] = t8;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    let t10;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        opacity: {
          duration: 0.15
        }
      };
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    let t11;
    if ($[17] !== fill || $[18] !== height || $[19] !== rx || $[20] !== ry || $[21] !== t6 || $[22] !== t9 || $[23] !== width || $[24] !== x || $[25] !== y) {
      t11 = <motion.rect animate={t6} fill={fill} height={height} initial={t7} rx={rx} ry={ry} style={t9} transition={t10} width={width} x={x} y={y} />;
      $[17] = fill;
      $[18] = height;
      $[19] = rx;
      $[20] = ry;
      $[21] = t6;
      $[22] = t9;
      $[23] = width;
      $[24] = x;
      $[25] = y;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    return t11;
  }
  let t4;
  if ($[27] !== height || $[28] !== innerHeight || $[29] !== isAnimated || $[30] !== isHorizontal || $[31] !== width || $[32] !== x || $[33] !== y) {
    t4 = isHorizontal ? {
      width: isAnimated ? width : 0,
      height,
      x: 0,
      y
    } : {
      width,
      height: isAnimated ? height : 0,
      x,
      y: isAnimated ? y : innerHeight
    };
    $[27] = height;
    $[28] = innerHeight;
    $[29] = isAnimated;
    $[30] = isHorizontal;
    $[31] = width;
    $[32] = x;
    $[33] = y;
    $[34] = t4;
  } else {
    t4 = $[34];
  }
  const animatedProps = t4;
  const t5 = isFaded ? fadedOpacity : 1;
  let t6;
  if ($[35] !== t5) {
    t6 = {
      opacity: t5
    };
    $[35] = t5;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  const t7 = `width ${barDuration}ms ${BAR_EASING}, height ${barDuration}ms ${BAR_EASING}, x ${barDuration}ms ${BAR_EASING}, y ${barDuration}ms ${BAR_EASING}`;
  let t8;
  if ($[37] !== t7) {
    t8 = {
      transition: t7
    };
    $[37] = t7;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: {
        duration: 0.15
      }
    };
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== animatedProps.height || $[41] !== animatedProps.width || $[42] !== animatedProps.x || $[43] !== animatedProps.y || $[44] !== fill || $[45] !== rx || $[46] !== ry || $[47] !== t6 || $[48] !== t8) {
    t10 = <motion.rect animate={t6} fill={fill} height={animatedProps.height} rx={rx} ry={ry} style={t8} transition={t9} width={animatedProps.width} x={animatedProps.x} y={animatedProps.y} />;
    $[40] = animatedProps.height;
    $[41] = animatedProps.width;
    $[42] = animatedProps.x;
    $[43] = animatedProps.y;
    $[44] = fill;
    $[45] = rx;
    $[46] = ry;
    $[47] = t6;
    $[48] = t8;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  return t10;
}
export function Bar(t0) {
  const $ = _c(56);
  const {
    dataKey,
    fill: t1,
    lineCap: t2,
    animate: t3,
    animationType: t4,
    fadedOpacity: t5,
    staggerDelay,
    stackGap: t6,
    groupGap: t7
  } = t0;
  const fill = t1 === undefined ? chartCssVars.linePrimary : t1;
  const lineCap = t2 === undefined ? "round" : t2;
  const animate = t3 === undefined ? true : t3;
  const animationType = t4 === undefined ? "grow" : t4;
  const fadedOpacity = t5 === undefined ? 0.3 : t5;
  const stackGap = t6 === undefined ? 0 : t6;
  const groupGap = t7 === undefined ? 4 : t7;
  const {
    data,
    yScale,
    innerHeight,
    isLoaded,
    barScale,
    bandWidth,
    hoveredBarIndex,
    setHoveredBarIndex,
    barXAccessor,
    lines,
    orientation,
    stacked,
    stackOffsets,
    animationDuration
  } = useChart();
  const totalAnimDuration = animationDuration || 1100;
  const staggerSpread = totalAnimDuration * 0.4;
  const calculatedStaggerDelay = staggerDelay ?? (data.length > 1 ? staggerSpread / 1000 / data.length : 0);
  const uniqueId = useId();
  const isHorizontal = orientation === "horizontal";
  let t8;
  let t9;
  if ($[0] !== dataKey) {
    t9 = l => l.dataKey === dataKey;
    $[0] = dataKey;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const idx = lines.findIndex(t9);
  t8 = idx >= 0 ? idx : 0;
  const seriesIndex = t8;
  const seriesCount = lines.length;
  const isLastSeries = seriesIndex === seriesCount - 1;
  let t10;
  bb0: {
    if (!bandWidth || seriesCount === 0) {
      t10 = 0;
      break bb0;
    }
    if (stacked) {
      t10 = bandWidth;
      break bb0;
    }
    const effectiveGroupGap = seriesCount > 1 ? groupGap : 0;
    t10 = (bandWidth - effectiveGroupGap * (seriesCount - 1)) / seriesCount;
  }
  const barWidth = t10;
  let t11;
  bb1: {
    if (typeof lineCap === "number") {
      t11 = lineCap;
      break bb1;
    }
    if (lineCap === "round" && barWidth) {
      t11 = Math.min(barWidth / 2, 8);
      break bb1;
    }
    t11 = 0;
  }
  const cornerRadius = t11;
  if (!(barScale && bandWidth && barXAccessor)) {
    console.warn("Bar component must be used within a BarChart");
    return null;
  }
  const t12 = `bar-series-${uniqueId}`;
  let t13;
  if ($[2] !== animate || $[3] !== animationType || $[4] !== barScale || $[5] !== barWidth || $[6] !== barXAccessor || $[7] !== calculatedStaggerDelay || $[8] !== cornerRadius || $[9] !== data || $[10] !== dataKey || $[11] !== fadedOpacity || $[12] !== fill || $[13] !== groupGap || $[14] !== hoveredBarIndex || $[15] !== innerHeight || $[16] !== isHorizontal || $[17] !== isLastSeries || $[18] !== isLoaded || $[19] !== seriesCount || $[20] !== seriesIndex || $[21] !== setHoveredBarIndex || $[22] !== stackGap || $[23] !== stackOffsets || $[24] !== stacked || $[25] !== totalAnimDuration || $[26] !== yScale) {
    let t14;
    if ($[28] !== animate || $[29] !== animationType || $[30] !== barScale || $[31] !== barWidth || $[32] !== barXAccessor || $[33] !== calculatedStaggerDelay || $[34] !== cornerRadius || $[35] !== dataKey || $[36] !== fadedOpacity || $[37] !== fill || $[38] !== groupGap || $[39] !== hoveredBarIndex || $[40] !== innerHeight || $[41] !== isHorizontal || $[42] !== isLastSeries || $[43] !== isLoaded || $[44] !== seriesCount || $[45] !== seriesIndex || $[46] !== setHoveredBarIndex || $[47] !== stackGap || $[48] !== stackOffsets || $[49] !== stacked || $[50] !== totalAnimDuration || $[51] !== yScale) {
      t14 = (d, i) => {
        const value = d[dataKey];
        if (typeof value !== "number") {
          return null;
        }
        const categoryValue = barXAccessor(d);
        const bandPos = barScale(categoryValue) ?? 0;
        let x;
        let y;
        let barHeight;
        let barW;
        if (isHorizontal) {
          const valuePos = yScale(value) ?? 0;
          barW = valuePos;
          barHeight = barWidth;
          if (stacked && stackOffsets) {
            const offset = stackOffsets.get(i)?.get(dataKey) ?? 0;
            x = yScale(offset) ?? 0;
            barW = valuePos - x;
            const gapOffset = seriesIndex * stackGap;
            x = x + gapOffset;
            if (!isLastSeries && stackGap > 0) {
              barW = Math.max(0, barW - stackGap);
            }
          } else {
            x = 0;
            seriesCount > 1 ? groupGap : 0;
          }
          y = stacked ? bandPos : bandPos + seriesIndex * (barWidth + (seriesCount > 1 ? groupGap : 0));
        } else {
          const valuePos_0 = yScale(value) ?? 0;
          barHeight = innerHeight - valuePos_0;
          barW = barWidth;
          if (stacked && stackOffsets) {
            const offset_0 = stackOffsets.get(i)?.get(dataKey) ?? 0;
            const offsetY = yScale(offset_0) ?? innerHeight;
            const gapOffset_0 = seriesIndex * stackGap;
            y = offsetY - barHeight - gapOffset_0;
            if (!isLastSeries && stackGap > 0) {
              barHeight = Math.max(0, barHeight - stackGap);
            }
          } else {
            y = valuePos_0;
            seriesCount > 1 ? groupGap : 0;
          }
          x = stacked ? bandPos : bandPos + seriesIndex * (barWidth + (seriesCount > 1 ? groupGap : 0));
        }
        const isFaded = hoveredBarIndex !== null && hoveredBarIndex !== i;
        const barKey = `bar-${dataKey}-${categoryValue}`;
        const applyRounding = !stacked || stackGap > 0 || isLastSeries;
        const effectiveRx = applyRounding ? cornerRadius : 0;
        const effectiveRy = applyRounding ? cornerRadius : 0;
        if (animate && !isLoaded) {
          return <AnimatedBar animationDuration={totalAnimDuration} animationType={animationType} fadedOpacity={fadedOpacity} fill={fill} height={barHeight} index={i} innerHeight={innerHeight} isFaded={isFaded} isHorizontal={isHorizontal} key={barKey} rx={effectiveRx} ry={effectiveRy} staggerDelay={calculatedStaggerDelay} width={barW} x={x} y={y} />;
        }
        return <motion.rect animate={{
          opacity: isFaded ? fadedOpacity : 1
        }} fill={fill} height={barHeight} key={barKey} onMouseEnter={() => setHoveredBarIndex?.(i)} onMouseLeave={() => setHoveredBarIndex?.(null)} rx={effectiveRx} ry={effectiveRy} style={{
          cursor: "pointer"
        }} transition={{
          opacity: {
            duration: 0.15
          }
        }} width={barW} x={x} y={y} />;
      };
      $[28] = animate;
      $[29] = animationType;
      $[30] = barScale;
      $[31] = barWidth;
      $[32] = barXAccessor;
      $[33] = calculatedStaggerDelay;
      $[34] = cornerRadius;
      $[35] = dataKey;
      $[36] = fadedOpacity;
      $[37] = fill;
      $[38] = groupGap;
      $[39] = hoveredBarIndex;
      $[40] = innerHeight;
      $[41] = isHorizontal;
      $[42] = isLastSeries;
      $[43] = isLoaded;
      $[44] = seriesCount;
      $[45] = seriesIndex;
      $[46] = setHoveredBarIndex;
      $[47] = stackGap;
      $[48] = stackOffsets;
      $[49] = stacked;
      $[50] = totalAnimDuration;
      $[51] = yScale;
      $[52] = t14;
    } else {
      t14 = $[52];
    }
    t13 = data.map(t14);
    $[2] = animate;
    $[3] = animationType;
    $[4] = barScale;
    $[5] = barWidth;
    $[6] = barXAccessor;
    $[7] = calculatedStaggerDelay;
    $[8] = cornerRadius;
    $[9] = data;
    $[10] = dataKey;
    $[11] = fadedOpacity;
    $[12] = fill;
    $[13] = groupGap;
    $[14] = hoveredBarIndex;
    $[15] = innerHeight;
    $[16] = isHorizontal;
    $[17] = isLastSeries;
    $[18] = isLoaded;
    $[19] = seriesCount;
    $[20] = seriesIndex;
    $[21] = setHoveredBarIndex;
    $[22] = stackGap;
    $[23] = stackOffsets;
    $[24] = stacked;
    $[25] = totalAnimDuration;
    $[26] = yScale;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[53] !== t12 || $[54] !== t13) {
    t14 = <g className={t12}>{t13}</g>;
    $[53] = t12;
    $[54] = t13;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  return t14;
}
Bar.displayName = "Bar";
export default Bar;